import {
  Box,
  Flex,
  VStack,
  Text,
  Button,
  List,
  ListItem,
  ListIcon,
  Image,
  HStack,
  useColorModeValue
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import landingImage from '../assets/Blank vindie landing page.jpg';

interface HeroBannerProps {
  fullWidth?: boolean;
}

const HeroBanner: React.FC<HeroBannerProps> = ({ fullWidth = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <Box
      w={fullWidth ? '100vw' : '100%'}
      maxW={fullWidth ? '100vw' : '100%'}
      position="relative"
      left={fullWidth ? { base: 'auto', lg: '50%' } : 'auto'}
      transform={fullWidth ? { base: 'none', lg: 'translateX(-50%)' } : 'none'}
      borderRadius={fullWidth ? '0' : { base: '0', lg: '2xl' }}
      overflow="hidden"
      boxShadow="2xl"
      mb={8}
    >
      {/* Background image */}
      <Image
        src={landingImage}
        alt="Vindie Stream landing"
        w="100%"
        h={{ base: '400px', sm: '520px', md: fullWidth ? '820px' : '640px', lg: fullWidth ? '920px' : '720px' }}
        objectFit="cover"
        filter="brightness(0.85)"
      />
      {/* Gradient overlay */}
      <Box
        position="absolute"
        inset={0}
        bgGradient={{
          base: 'linear(to-br, rgba(17, 17, 39, 0.92) 0%, rgba(31, 38, 84, 0.88) 40%, rgba(31, 38, 84, 0.65) 60%, transparent 75%)',
          md: 'linear(to-br, rgba(17, 17, 39, 0.92) 0%, rgba(31, 38, 84, 0.9) 35%, rgba(31, 38, 84, 0.6) 55%, transparent 70%)'
        }}
      />
      {/* Content */}
      <Flex
        position="absolute"
        inset={0}
        align="center"
        justify="flex-start"
        px={{ base: 4, sm: 6, md: 12, lg: 16 }}
        py={{ base: 6, sm: 10, md: 16 }}
      >
      <VStack align="flex-start" spacing={{ base: 3, sm: 5, md: 6 }} maxW={{ base: '100%', md: '500px', lg: '600px' }}>
        {!user ? (
          <>
            <Text fontSize={{ base: '2xl', sm: '4xl', md: '5xl', lg: '6xl' }} fontWeight="black" color="white" lineHeight="0.9">
              Your Platform for Independent Content
            </Text>
            <Text fontSize={{ base: 'md', sm: 'lg', md: 'xl' }} color="whiteAlpha.900" fontWeight="medium">
              Watch, create, and share original movies, series, books, comics, and shorts. Join a community of independent creators and discover unique stories.
            </Text>
            <List spacing={{ base: 2, sm: 3 }} color="whiteAlpha.900" fontSize={{ base: 'sm', sm: 'md', md: 'lg' }}>
              {[
                'Watch unlimited independent content',
                'Upload and share your own creations',
                'Support creators you love',
                'Download and watch offline'
              ].map(item => (
                <ListItem key={item} display="flex" alignItems="center" gap={2}>
                  <ListIcon as={ChevronRightIcon} color="purple.300" />
                  {item}
                </ListItem>
              ))}
            </List>
            <HStack spacing={3} flexWrap="wrap">
              <Button
                size={{ base: 'sm', sm: 'md', md: 'lg' }}
                colorScheme="purple"
                px={{ base: 4, sm: 6, md: 8 }}
                onClick={() => navigate('/signup')}
              >
                Get Started Free
              </Button>
              <Button
                size={{ base: 'sm', sm: 'md', md: 'lg' }}
                variant="outline"
                colorScheme="whiteAlpha"
                borderColor="whiteAlpha.500"
                color="white"
                px={{ base: 4, sm: 6, md: 8 }}
                _hover={{ bg: 'whiteAlpha.200', borderColor: 'whiteAlpha.700' }}
                onClick={() => navigate('/search')}
              >
                Explore Content
              </Button>
            </HStack>
          </>
        ) : (
          <>
            <Text fontSize={{ base: '2xl', sm: '4xl', md: '5xl', lg: '6xl' }} fontWeight="black" color="white" lineHeight="0.9">
              Welcome Back!
            </Text>
            <Text fontSize={{ base: 'md', sm: 'lg', md: 'xl' }} color="whiteAlpha.900" fontWeight="medium">
              Continue discovering amazing independent content or share your own creations.
            </Text>
            <HStack spacing={3} flexWrap="wrap">
              <Button
                size={{ base: 'sm', sm: 'md', md: 'lg' }}
                colorScheme="purple"
                px={{ base: 4, sm: 6, md: 8 }}
                onClick={() => navigate('/search')}
              >
                Browse Content
              </Button>
              <Button
                size={{ base: 'sm', sm: 'md', md: 'lg' }}
                variant="outline"
                colorScheme="whiteAlpha"
                borderColor="whiteAlpha.500"
                color="white"
                px={{ base: 4, sm: 6, md: 8 }}
                _hover={{ bg: 'whiteAlpha.200', borderColor: 'whiteAlpha.700' }}
                onClick={() => navigate('/upload/category')}
              >
                Upload Content
              </Button>
            </HStack>
          </>
        )}
      </VStack>
      </Flex>
    </Box>
  );
};

export default HeroBanner;
